import {
  AHText,
  AHTextFonts,
  Box,
  TouchableBox,
  useScaleFactor,
  useTime,
} from "arkh";
import { DateTime } from "luxon";
import React, { useEffect, useMemo, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { useWidgetLayout } from "../WidgetLayoutContext";

import { graphql, useFragment } from "react-relay";

import { WidgetIpoolIntegration_widgetIntegrationIpool$key } from "./__generated__/WidgetIpoolIntegration_widgetIntegrationIpool.graphql";
import { useSetWidgetFocus } from "../useSetFocus";
import { EmployeeShifts } from "./WidgetIpoolIntegrationEmployeeShifts";
import { WidgetIpoolIntegrationEmployeeNamePanel_ipoolEmployee$key } from "./__generated__/WidgetIpoolIntegrationEmployeeNamePanel_ipoolEmployee.graphql";
import { useInterval } from "../../useInterval";

const totalHours = 19;
export const WidgetIpoolIntegration = (props: {
  widget: WidgetIpoolIntegration_widgetIntegrationIpool$key;
  storecastId?: string;
  mode?: "FOCUS" | "IDLE";

  interactive?: undefined | false;
}) => {
  const widget = useFragment(
    graphql`
      fragment WidgetIpoolIntegration_widgetIntegrationIpool on WidgetIntegrationIpool {
        id
        overviewSize
        integration {
          employeesWithShifts {
            id
            ...WidgetIpoolIntegrationEmployeeNamePanel_ipoolEmployee
            shifts {
              id
              endAt
            }
            ...WidgetIpoolIntegrationEmployeeShifts_integrationIpoolEmployee
          }
        }
      }
    `,
    props.widget
  );
  const setFocus = useSetWidgetFocus(widget.id, props.storecastId);
  const layout = useWidgetLayout();
  const width = layout.width;

  const employeePaneWidth = Math.floor(width / 6);
  const boxWidth = (width - employeePaneWidth) / (totalHours * 2);

  const sf = useScaleFactor();

  const doubleListHeight = Math.floor(35 * sf);
  const isDoubleHeight =
    (widget.integration.employeesWithShifts.length - 1) * doubleListHeight <
    layout.height;

  const listItemHeight = Math.floor(
    isDoubleHeight ? doubleListHeight : 20 * sf
  );

  const headerHeight = listItemHeight + 5 * sf;

  const startD = useMemo(
    () =>
      DateTime.local().set({
        hour: 5,
        minute: 0,
        second: 0,
        millisecond: 0,
      }),
    []
  );

  const now = useTime(60000);

  const firstNotEndedEmployee = useMemo(
    () =>
      Math.max(
        0,
        widget.integration.employeesWithShifts.findIndex((employee) => {
          return (
            employee.shifts.findIndex((shift) => {
              const isActive = DateTime.fromISO(shift.endAt) > now;
              return isActive;
            }) !== -1
          );
        }) - 1
      ),
    [now, widget.integration.employeesWithShifts]
  );

  const scrollRef = React.useRef<ScrollView>(null);

  useEffect(() => {
    if (firstNotEndedEmployee !== -1 && props.interactive !== false) {
      scrollRef.current?.scrollTo({
        x: 0,
        y: firstNotEndedEmployee * listItemHeight,
        animated: true,
      });
    }
  }, [firstNotEndedEmployee, listItemHeight, props.interactive]);

  useEffect(() => {
    // we want to scroll when the widget is resized
    const timeout = setTimeout(() => {
      if (firstNotEndedEmployee !== -1 && props.interactive !== false) {
        scrollRef.current?.scrollTo({
          x: 0,
          y: firstNotEndedEmployee * listItemHeight,
          animated: true,
        });
      }
    }, 4000);

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    widget.overviewSize, // we want to scroll whe whe resize the widget
  ]);

  const [mode, setMode] = useState<"TEAM" | "TIME">("TEAM");
  useInterval(
    () => {
      setMode((mode) => (mode === "TEAM" ? "TIME" : "TEAM"));
    },
    props.interactive === false ? null : 3500
  );
  const body = (
    <>
      <EmployeeNamePanel
        employeePaneWidth={employeePaneWidth}
        employees={widget.integration.employeesWithShifts}
        isDoubleHeight={isDoubleHeight}
        listItemHeight={listItemHeight}
      />
      <View>
        <TimeBackgroundLines boxWidth={boxWidth} />
        <View
          style={{
            position: "absolute",
            height: "100%",
            width: 1,

            left: (now.diff(startD).as("minutes") / 30) * boxWidth,
          }}
        />

        <View style={{ position: "relative" }}>
          <View
            style={{ position: "absolute", opacity: mode === "TIME" ? 1 : 0 }}
          >
            <EmployeeShifts
              shifts={widget.integration.employeesWithShifts}
              listHeight={listItemHeight}
              isDoubleHeight={isDoubleHeight}
              boxWidth={boxWidth}
              now={now}
              startD={startD}
              mode="TEAM"
            />
          </View>
          <View
            style={{ position: "absolute", opacity: mode === "TEAM" ? 1 : 0 }}
          >
            <EmployeeShifts
              shifts={widget.integration.employeesWithShifts}
              listHeight={listItemHeight}
              isDoubleHeight={isDoubleHeight}
              boxWidth={boxWidth}
              now={now}
              startD={startD}
              mode="TIME"
            />
          </View>
        </View>
      </View>
    </>
  );
  return (
    <TouchableBox
      disabled={props.mode !== "FOCUS"}
      onPress={props.mode === "FOCUS" ? () => setFocus() : undefined}
      color="surface"
      style={{
        flex: 1,
        width: "100%",
        height: "100%",
        flexDirection: "column",
      }}
    >
      <TimeHeader
        boxWidth={boxWidth}
        employeePaneWidth={employeePaneWidth}
        headerHeight={headerHeight}
      />
      {props.interactive === false ? (
        <View style={{ height: "100%", flexDirection: "row" }}>{body}</View>
      ) : (
        <ScrollView
          showsVerticalScrollIndicator={false}
          style={{ height: "100%" }}
          contentContainerStyle={{ flexDirection: "row", minHeight: "100%" }}
          ref={scrollRef}
        >
          {body}
        </ScrollView>
      )}
    </TouchableBox>
  );
};

const TimeBackgroundLines = React.memo(_TimeBackgroundLines);
function _TimeBackgroundLines(props: { boxWidth: number }) {
  return (
    <>
      {new Array(totalHours * 2).fill(0).map((_, i) => {
        return (
          <View
            key={i + ""}
            style={{
              position: "absolute",
              height: "100%",
              width: StyleSheet.hairlineWidth,
              backgroundColor: i % 2 === 0 ? "#eee" : "#ccc",
              left: (i + 1) * props.boxWidth,
            }}
          />
        );
      })}
    </>
  );
}
const TimeHeader = React.memo(_TimeHeader);
function _TimeHeader(props: {
  headerHeight: number;
  employeePaneWidth: number;
  boxWidth: number;
}) {
  const sf = useScaleFactor();
  return (
    <Box
      color="surface"
      style={{
        flexDirection: "row",
        height: props.headerHeight,
        left: props.employeePaneWidth,
      }}
    >
      {new Array(totalHours - 1).fill(0).map((_, i) => {
        return (
          <AHText
            key={i + ""}
            style={{
              width: props.boxWidth * 2,
              flex: 0,
              left: i * props.boxWidth * 2 + props.boxWidth,
              textAlign: "center",
              top: 8 * sf,
              fontSize: Math.round(11 * sf),
              position: "absolute",
            }}
            numberOfLines={1}
            font={AHTextFonts.BOLDER}
            color="darkerText"
            type="detail"
          >
            {("00" + Math.floor(i + 6)).slice(-2)}
          </AHText>
        );
      })}
    </Box>
  );
}

const EmployeeNamePanel = React.memo(_EmployeeNamePanel);
function _EmployeeNamePanel(props: {
  employeePaneWidth: number;
  isDoubleHeight: boolean;
  listItemHeight: number;
  employees: WidgetIpoolIntegrationEmployeeNamePanel_ipoolEmployee$key;
}) {
  const sf = useScaleFactor();
  const employees = useFragment(
    graphql`
      fragment WidgetIpoolIntegrationEmployeeNamePanel_ipoolEmployee on IntegrationIpoolEmployee
      @relay(plural: true) {
        id
        name
      }
    `,
    props.employees
  );

  const shouldShouldFullName =
    props.employeePaneWidth * sf > 100 || props.isDoubleHeight;
  return (
    <View
      style={{
        width: props.employeePaneWidth,
        height: "100%",
      }}
    >
      {employees.map((e, i) => {
        const names = e.name.trim().split(" ");
        return (
          <Box
            key={e.id}
            style={{
              height: props.listItemHeight,
              justifyContent: "center",
            }}
          >
            <AHText
              font={AHTextFonts.BOLD}
              numberOfLines={props.isDoubleHeight ? 2 : 1}
              spaceLeft="xsmall"
              style={{ fontSize: Math.round(12 * sf) }}
              type="detail"
              ellipsizeMode="tail"
              color="darkerText"
            >
              {shouldShouldFullName ? (
                e.name
              ) : (
                <>
                  {names[0]} {names.length > 1 ? names[1][0] : ""}
                </>
              )}
            </AHText>
          </Box>
        );
      })}
    </View>
  );
}
